import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  Icon,
  LinearProgress,
  Notice,
  RadioCard,
  SelectField,
  TextField,
  iconCopy,
  iconShare,
  useSnackbar,
  DateField,
  FieldGroup,
  ErrorText,
} from '@sauda/ui/kit';
import { useAuth } from '../../auth/AuthContext.js';
import { api } from '../../lib/api.js';
import { istDateInput } from '../../lib/money-input.js';
import { whatsappLink } from '../../lib/share-text.js';
import type { DealDetailView, PartnerView } from '../../lib/types.js';

const MAX_BYTES = 15 * 1024 * 1024;

/** Registration: either the broker registers it and uploads the document, or a partner gets a job link. */
export function RegistrationSheet({
  deal,
  open,
  onClose,
}: {
  deal: DealDetailView;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const auth = useAuth();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const canPartner = auth.allowed('partners.manage');
  const [route, setRoute] = useState<'own' | 'partner'>('own');
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(istDateInput());
  const [sro, setSro] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState('');
  const [jobLink, setJobLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const partners = useQuery({
    queryKey: ['partners', auth.agencyId],
    queryFn: () => api<PartnerView[]>('/partners'),
    enabled: open && canPartner && route === 'partner',
  });

  const pick = (next: File | null) => {
    setFile(next);
    if (next === null) return setFileError(null);
    if (next.type !== 'application/pdf') return setFileError(t('deal.fileWrong'));
    if (next.size > MAX_BYTES) return setFileError(t('deal.fileBig'));
    setFileError(null);
  };

  const register = useMutation({
    mutationFn: async () => {
      if (file === null) throw new Error('no file');
      const ticket = await api<{ upload: { url: string; fields: Record<string, string> } }>(
        `/deals/${deal.id}/registration`,
        { method: 'POST', body: { number, date, sroOffice: sro } },
      );
      const data = new FormData();
      for (const [name, value] of Object.entries(ticket.upload.fields)) data.append(name, value);
      data.append('file', file);
      const res = await fetch(ticket.upload.url, {
        method: 'POST',
        body: data,
        credentials: 'omit',
      });
      if (!res.ok) throw new Error('upload failed');
      return api(`/deals/${deal.id}/registration/confirm`, { method: 'POST', body: {} });
    },
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['deal', deal.id] }),
        cache.invalidateQueries({ queryKey: ['agreement', deal.id] }),
        cache.invalidateQueries({ queryKey: ['deals'] }),
      ]);
      onClose();
      snack({ text: t('deal.registered') });
    },
    // The typed fields stay, so a failed upload is a retry, not a restart.
    onError: () => setError(t('deal.registrationFailed')),
  });
  const sendJob = useMutation({
    mutationFn: () =>
      api<{ link: string }>(`/deals/${deal.id}/partner-jobs`, {
        method: 'POST',
        body: { partnerId },
      }),
    onSuccess: async (result) => {
      setJobLink(result.link);
      await cache.invalidateQueries({ queryKey: ['jobs', deal.id] });
    },
    onError: () => setError(t('deal.generic')),
  });

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      label={t('deal.registrationTitle')}
      title={t('deal.registrationTitle')}
      closeLabel={t('app.close')}
    >
      <div className="k-stack">
        {canPartner ? (
          <FieldGroup legend={t('deal.registrationRoute')}>
            <RadioCard
              name="route"
              label={t('deal.routeOwn')}
              description={t('deal.routeOwnBody')}
              checked={route === 'own'}
              onChange={() => setRoute('own')}
            />
            <RadioCard
              name="route"
              label={t('deal.routePartner')}
              description={t('deal.routePartnerBody')}
              checked={route === 'partner'}
              onChange={() => setRoute('partner')}
            />
          </FieldGroup>
        ) : null}
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        {route === 'own' || !canPartner ? (
          <>
            <TextField
              label={t('deals.regNo')}
              value={number}
              onChange={(e) => setNumber(e.target.value)}
            />
            <DateField label={t('deals.regDate')} value={date} onChange={setDate} />
            <TextField
              label={t('deals.sro')}
              value={sro}
              onChange={(e) => setSro(e.target.value)}
            />
            <div className="k-field">
              <label htmlFor="registered-file" className="k-field__label k-t-label-sm">
                {t('deals.registeredFile')}
              </label>
              <input
                id="registered-file"
                data-testid="registered-file"
                type="file"
                accept="application/pdf"
                onChange={(e) => pick(e.target.files?.[0] ?? null)}
                aria-invalid={fileError !== null || undefined}
              />
              {fileError === null ? null : <ErrorText>{fileError}</ErrorText>}
            </div>
            {register.isPending ? <LinearProgress label={t('deal.uploading')} /> : null}
            <Button
              size="lg"
              block
              loading={register.isPending}
              disabled={
                file === null ||
                fileError !== null ||
                number.trim() === '' ||
                date === '' ||
                sro.trim() === ''
              }
              onClick={() => register.mutate()}
            >
              {t('deals.completeRegistration')}
            </Button>
          </>
        ) : (
          <>
            <SelectField
              label={t('deals.partner')}
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              placeholder={t('deals.choosePartner')}
              options={(partners.data ?? [])
                .filter((p) => p.active)
                .map((p) => ({ value: p.id, label: p.name }))}
            />
            <Button
              size="lg"
              block
              loading={sendJob.isPending}
              disabled={partnerId === ''}
              onClick={() => sendJob.mutate()}
            >
              {t('deals.sendPartner')}
            </Button>
            {jobLink === null ? null : (
              <div className="link-row">
                <a
                  className="k-btn k-btn--filled k-btn--md k-layer k-t-label-lg"
                  href={whatsappLink(
                    t('deal.partnerMessage', { property: deal.propertyTitle ?? '', link: jobLink }),
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon icon={iconShare} size={18} />
                  {t('deal.shareWhatsapp')}
                </a>
                <Button
                  variant="tonal"
                  onClick={() => {
                    void navigator.clipboard?.writeText(jobLink);
                    snack({ text: t('deal.linkCopiedPlain') });
                  }}
                >
                  <Icon icon={iconCopy} size={18} />
                  {t('deal.copyLink')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </BottomSheet>
  );
}
